import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Building2, Users, User, Loader2, ExternalLink } from 'lucide-react';
import UnifiedHeader from '../components/UnifiedHeader';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const SearchPage = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [query, setQuery] = useState('');
  const [userType, setUserType] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isBrandUser = user?.user_type === 'brand';

  useEffect(() => {
    const debounce = setTimeout(() => {
      // Always search when query or userType changes (including clearing userType)
      searchProfiles();
    }, 300);

    return () => clearTimeout(debounce);
  }, [query, userType]);

  useEffect(() => {
    // Load initial results
    searchProfiles();
  }, []);

  const searchProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      // Only add user_type param if it's not empty
      if (userType && userType !== '') {
        params.append('user_type', userType);
      }

      const headers = {};
      // Include auth token so brands can see Instagram links
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_URL}/api/search?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setResults(data.results);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'brand': return Building2;
      case 'influencer': return Users;
      default: return User;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
      <UnifiedHeader />

      {/* Main Content with top padding for fixed nav */}
      <div className="pt-20 flex-1">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Discover Brands & Influencers</h1>

          {/* Search Bar */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or handle..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                />
              </div>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500"
              >
                <option value="">All Types</option>
                <option value="brand">Brands</option>
                <option value="influencer">Influencers</option>
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No profiles found. Try a different search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {results.map((profile) => {
                const TypeIcon = getTypeIcon(profile.user_type);
                return (
                  <Link
                    key={profile.handle}
                    to={profile.user_type === 'influencer' ? `/influencer/${profile.handle}` : `/profile/${profile.handle}`}
                    className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start space-x-4"
                  >
                    <div className={`w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden ${
                      profile.user_type === 'brand' ? 'bg-blue-100' :
                      profile.user_type === 'influencer' ? 'bg-purple-100' : 'bg-rose-100'
                    }`}>
                      {profile.profile_image ? (
                        <img 
                          src={profile.profile_image.startsWith('data:') ? profile.profile_image : 
                               profile.profile_image.startsWith('http') ? profile.profile_image :
                               `data:image/png;base64,${profile.profile_image}`}
                          alt={profile.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<svg class="w-7 h-7 ${
                              profile.user_type === 'brand' ? 'text-blue-500' :
                              profile.user_type === 'influencer' ? 'text-purple-500' : 'text-rose-500'
                            }" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>`;
                          }}
                        />
                      ) : (
                        <TypeIcon className={`w-7 h-7 ${
                          profile.user_type === 'brand' ? 'text-blue-500' :
                          profile.user_type === 'influencer' ? 'text-purple-500' : 'text-rose-500'
                        }`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{profile.name}</h3>
                      <p className="text-sm text-gray-500 truncate">@{profile.handle}</p>
                      {profile.bio && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2 break-all">{profile.bio}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          profile.user_type === 'brand' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {profile.user_type}
                        </span>
                        {/* Show Instagram link to all logged-in users viewing influencers */}
                        {isAuthenticated && profile.user_type === 'influencer' && profile.instagram_link && (
                          <a
                            href={profile.instagram_link.startsWith('http') ? profile.instagram_link : `https://instagram.com/${profile.instagram_link.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
                          >
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                            Instagram
                          </a>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default SearchPage;
